import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { useParams } from 'react-router-dom';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';

interface UniversityPaymentData {
    UniversityPaymentInvoiceRaised: boolean;
    UniversityPaymentInvoiceDate: string;
    UniversityPaymentInvoiceAmount: number | null;
    UniversityPaymentStatus: string;
    UniversityPaymentReceiptUrl: string;
    UniversityAdmissionDate: string;
}

const UniversityPaymentUI = ({
    write,
    initialData
}: {
    write: boolean;
    initialData?: Partial<UniversityPaymentData>;
}) => {
    const { currentSubAgent } = useSubAgentAuth();
    const { userData } = useAuth();
    const { leadId } = useParams();
    const companyId = currentSubAgent?.companyId || userData?.companyId;

    const [paymentData, setPaymentData] = useState<UniversityPaymentData>({
        UniversityPaymentInvoiceRaised: false,
        UniversityPaymentInvoiceDate: '',
        UniversityPaymentInvoiceAmount: null,
        UniversityPaymentStatus: 'payment pending',
        UniversityPaymentReceiptUrl: '',
        UniversityAdmissionDate: '',
        ...initialData
    });

    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [isInvoiceDropdownOpen, setIsInvoiceDropdownOpen] = useState(false);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const invoiceDropdownRef = useRef<HTMLDivElement>(null);
    const statusDropdownRef = useRef<HTMLDivElement>(null);

    const invoiceRaisedOptions = ['Yes', 'No'];
    const paymentStatusOptions = ['payment initiated', 'payment received', 'payment pending'];

    useEffect(() => {
        const fetchPaymentData = async () => {
            if (!companyId || !leadId) return;

            try {
                const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
                const docSnap = await getDoc(leadRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setPaymentData({
                        UniversityPaymentInvoiceRaised: data.UniversityPaymentInvoiceRaised || false,
                        UniversityPaymentInvoiceDate: data.UniversityPaymentInvoiceDate || '',
                        UniversityPaymentInvoiceAmount: data.UniversityPaymentInvoiceAmount || null,
                        UniversityPaymentStatus: data.UniversityPaymentStatus || 'payment pending',
                        UniversityPaymentReceiptUrl: data.UniversityPaymentReceiptUrl || '',
                        UniversityAdmissionDate: data.UniversityAdmissionDate || ''
                    });
                }
            } catch (error) {
                console.error('Error fetching payment data:', error);
                setErrorMessage('Failed to load payment data');
            } finally {
                setIsFetching(false);
            }
        };

        fetchPaymentData();
    }, [companyId, leadId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (invoiceDropdownRef.current && !invoiceDropdownRef.current.contains(event.target as Node)) {
                setIsInvoiceDropdownOpen(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setIsStatusDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({
            ...prev,
            [name]: name.includes('Amount') ?
                (value === '' ? null : parseFloat(value)) :
                value
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setReceiptFile(e.target.files[0]);
        }
    };

    const toggleInvoiceDropdown = () => setIsInvoiceDropdownOpen(!isInvoiceDropdownOpen);
    const toggleStatusDropdown = () => setIsStatusDropdownOpen(!isStatusDropdownOpen);

    const handleInvoiceSelect = (option: string) => {
        const isInvoiceRaised = option === 'Yes';
        setPaymentData(prev => ({
            ...prev,
            UniversityPaymentInvoiceRaised: isInvoiceRaised,
            ...(!isInvoiceRaised ? {
                UniversityPaymentInvoiceDate: '',
                UniversityPaymentInvoiceAmount: null,
                UniversityPaymentStatus: 'payment pending',
                UniversityPaymentReceiptUrl: ''
            } : {})
        }));
        setIsInvoiceDropdownOpen(false);
    };

    const handleStatusSelect = (status: string) => {
        setPaymentData(prev => ({
            ...prev,
            UniversityPaymentStatus: status
        }));
        setIsStatusDropdownOpen(false);
    };

    const savePaymentData = async () => {
        if (!write || !companyId || !leadId) {
            setErrorMessage(!companyId ? 'Company ID is missing' : 'Lead ID is missing');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            const updateData: Partial<UniversityPaymentData> = {
                UniversityPaymentInvoiceRaised: paymentData.UniversityPaymentInvoiceRaised,
                UniversityPaymentStatus: paymentData.UniversityPaymentStatus,
                UniversityAdmissionDate: paymentData.UniversityAdmissionDate,
                ...(paymentData.UniversityPaymentInvoiceRaised ? {
                    UniversityPaymentInvoiceDate: paymentData.UniversityPaymentInvoiceDate,
                    UniversityPaymentInvoiceAmount: paymentData.UniversityPaymentInvoiceAmount,
                    UniversityPaymentReceiptUrl: receiptFile ? '' : paymentData.UniversityPaymentReceiptUrl
                } : { UniversityPaymentReceiptUrl: '' })
            };

            if (receiptFile) {
                const storagePath = `student_documents/${companyId}/${leadId}/University_receipts/${receiptFile.name}`;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, receiptFile);
                updateData.UniversityPaymentReceiptUrl = await getDownloadURL(storageRef);
            }

            await updateDoc(leadRef, updateData);
            setSuccessMessage('Payment details saved successfully!');
            setReceiptFile(null);
        } catch (error) {
            console.error('Error saving payment details:', error);
            setErrorMessage('Failed to save payment details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    function extractFileNameFromUrl(url: string): string | null {
        if (!url) return null;

        try {
            const decodedUrl = decodeURIComponent(url);
            const fileNameWithParams = decodedUrl.split('/').pop() || '';
            const fileName = fileNameWithParams.split('?')[0];
            return fileName || null;
        } catch {
            return null;
        }
    }

    const sectionClass = "mb-8 bg-white rounded-lg p-6 shadow-sm border border-gray-300";
    const sectionHeaderClass = "text-lg font-semibold text-sky-950 mb-4 pb-2 border-b border-gray-200";
    const inputClass = "w-full py-2 px-4 border border-gray-500 rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-500";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    if (isFetching) {
        return <div className="p-8">Loading payment data...</div>;
    }

    return (
        <div className="p-8">
            <div className={sectionClass}>
                <h2 className={sectionHeaderClass}>University Payout Details</h2>

                {successMessage && (
                    <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
                        {successMessage}
                    </div>
                )}
                {errorMessage && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                        {errorMessage}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="relative" ref={invoiceDropdownRef}>
                        <label htmlFor="UniversityPaymentInvoiceRaised" className={labelClass}>
                            Invoice Raised to University
                        </label>
                        <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                            <div className="relative">
                                <input
                                    type="text"
                                    id="UniversityPaymentInvoiceRaised"
                                    value={paymentData.UniversityPaymentInvoiceRaised ? 'Yes' : 'No'}
                                    onClick={toggleInvoiceDropdown}
                                    className={`w-full py-2 pl-10 pr-16 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                    readOnly
                                    disabled={!write}
                                />
                            </div>
                        </div>
                        <button
                            type="button"
                            className="absolute right-3 top-12 transform -translate-y-1/2"
                            onClick={toggleInvoiceDropdown}
                            disabled={!write}
                        >
                            <span className={`w-7 h-7 transition-transform ${isInvoiceDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                        </button>
                        {isInvoiceDropdownOpen && (
                            <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10">
                                {invoiceRaisedOptions.map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => handleInvoiceSelect(option)}
                                        className="w-full py-2 px-4 text-left hover:bg-gray-100"
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {paymentData.UniversityPaymentInvoiceRaised && (
                        <>
                            <div>
                                <label htmlFor="UniversityAdmissionDate" className={labelClass}>
                                    Admission Date
                                </label>
                                <input
                                    type="date"
                                    id="UniversityAdmissionDate"
                                    name="UniversityAdmissionDate"
                                    value={paymentData.UniversityAdmissionDate}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    disabled={!write}
                                />
                            </div>

                            <div>
                                <label htmlFor="UniversityPaymentInvoiceDate" className={labelClass}>
                                    Date of Invoice
                                </label>
                                <input
                                    type="date"
                                    id="UniversityPaymentInvoiceDate"
                                    name="UniversityPaymentInvoiceDate"
                                    value={paymentData.UniversityPaymentInvoiceDate}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                    disabled={!write}
                                />
                            </div>

                            <div>
                                <label htmlFor="UniversityPaymentInvoiceAmount" className={labelClass}>
                                    Invoice Amount
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                    <input
                                        type="number"
                                        id="UniversityPaymentInvoiceAmount"
                                        name="UniversityPaymentInvoiceAmount"
                                        value={paymentData.UniversityPaymentInvoiceAmount || ''}
                                        onChange={handleInputChange}
                                        className={`${inputClass} pl-8`}
                                        disabled={!write}
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="relative" ref={statusDropdownRef}>
                                <label htmlFor="UniversityPaymentStatus" className={labelClass}>
                                    University Payment Status
                                </label>
                                <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id="UniversityPaymentStatus"
                                            value={paymentData.UniversityPaymentStatus}
                                            onClick={toggleStatusDropdown}
                                            className={`w-full py-2 pl-10 pr-16 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                            readOnly
                                            disabled={!write}
                                        />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="absolute right-3 top-12 transform -translate-y-1/2"
                                    onClick={toggleStatusDropdown}
                                    disabled={!write}
                                >
                                    <span className={`w-7 h-7 transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                                </button>
                                {isStatusDropdownOpen && (
                                    <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10">
                                        {paymentStatusOptions.map((option) => (
                                            <button
                                                key={option}
                                                onClick={() => handleStatusSelect(option)}
                                                className="w-full py-2 px-4 text-left hover:bg-gray-100"
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label htmlFor="receipt" className={labelClass}>
                                    Upload Receipt/Document
                                </label>
                                <div className="flex items-center">
                                    <input
                                        type="file"
                                        id="receipt"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        disabled={!write}
                                    />
                                    <label
                                        htmlFor="receipt"
                                        className={`px-4 py-2 bg-gray-100 rounded-l-3xl border border-gray-300 ${!write ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-200'
                                            }`}
                                    >
                                        Choose File
                                    </label>
                                    <input
                                        type="text"
                                        value={
                                            receiptFile?.name ||
                                            extractFileNameFromUrl(paymentData.UniversityPaymentReceiptUrl) ||
                                            'No file chosen'
                                        }
                                        className={`flex-1 py-2 px-4 border border-gray-300 border-l-0 rounded-r-3xl bg-white ${!write ? 'cursor-not-allowed' : 'cursor-pointer'
                                            }`}
                                        readOnly
                                    />
                                </div>
                                {paymentData.UniversityPaymentReceiptUrl && !receiptFile && (
                                    <div className="mt-1">
                                        <a
                                            href={paymentData.UniversityPaymentReceiptUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 text-sm inline-block"
                                        >
                                            View document: {extractFileNameFromUrl(paymentData.UniversityPaymentReceiptUrl)}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {write && (
                    <div className="flex justify-center mt-8">
                        <button
                            onClick={savePaymentData}
                            disabled={isLoading}
                            className={`px-8 py-3 text-white bg-gradient-to-r from-[#3483BE] to-[#02385E] rounded-3xl font-medium hover:opacity-90 transition-opacity ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
                                }`}
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UniversityPaymentUI;